import {
  drawGuides,
  getAnchorPoints,
  getGuides,
  getLineGuideStops,
} from 'dataloader/utils/guideLines'
import Konva from 'konva'
import { round } from 'lodash'
import React, { useState } from 'react'
import { Group, Line, Rect } from 'react-konva'
import { connect } from 'react-redux'
import action from 'store/actions'
import { incrementMaterialPresets } from 'store/materialPresets/actions'
import {
  getAutoIncrementPresetsEnabled,
  getDescriptions,
  getDescriptionsData,
  getDrawings,
  getMaterialTypePresets,
  getSnappingDistance,
  getSnappingEnabled,
} from 'store/selectors'
import randomId from 'utils/randomId'
import { throttleRaf } from 'utils/throttleRaf'
import withSelectedValues, { DataloaderInfo } from 'utils/withSelectedValues'

interface OwnProps {
  width: number
  height: number
  scale: { x: number; y: number }
}

interface StateProps {
  descriptions: string[]
  descriptionsData: ReduxStore.Materials.Data.DescriptionData[]
  drawings: ReduxStore.Drawings.Data.Drawing[]
  snappingEnabled: boolean
  presets: ReduxStore.MaterialPresets.MaterialPreset[]
  autoIncrementEnabled: boolean
  snappingDistance: number
  options: any
}

interface DispatchProps {
  dispatchAddMaterials: (m: ReduxStore.Materials.Data.IMaterial) => void
  incrementPresets: (materialTypeId: number) => void
}

type Props = OwnProps & StateProps & DispatchProps & DataloaderInfo

interface Point {
  x: number
  y: number
}

const LineTool: React.FunctionComponent<Props> = props => {
  const {
    width,
    height,
    descriptions,
    dispatchAddMaterials,
    incrementPresets,
    selectedDrawing,
    selectedProject,
    selectedMaterialType,
    drawings,
    scale,
    autoIncrementEnabled,
    options
  } = props

  // State to track line drawing
  const [isDrawing, setIsDrawing] = useState(false)
  const [startPoint, setStartPoint] = useState<Point | null>(null)
  const [currentLine, setCurrentLine] = useState<number[] | null>(null)

  // Safely extract the stroke width from options
  const strokeWidth = options && typeof options.strokeWidth === 'number' ? options.strokeWidth : 2

  const getObjectSnappingEdges = (evt: Konva.KonvaEventObject<MouseEvent>) => {
    const pos = eventToCoordinates(evt)
    if (pos.x && pos.y) {
      return {
        vertical: [
          {
            guide: Math.round(pos.x),
            offset: 0,
            snap: 'start',
          },
        ],
        horizontal: [
          {
            guide: Math.round(pos.y),
            offset: 0,
            snap: 'start',
          },
        ],
      }
    }
  }

  const handleMouseDown = (evt: Konva.KonvaEventObject<MouseEvent>) => {
    // Ignore middle mouse button (button = 1) for panning
    if (evt.evt.button === 1) {
      // Let the event propagate to allow for panning
      return;
    }
    
    evt.evt.stopPropagation()
    
    // Only handle left-clicks
    if (evt.evt.button !== 0) {
      return;
    }
    
    const pos = eventToCoordinates(evt)
    
    // Apply snapping if enabled
    if (props.snappingEnabled) {
      const lineGuideStops = getLineGuideStops(evt.target, pos, props.snappingDistance)
      const itemBounds = getObjectSnappingEdges(evt)
      if (itemBounds) {
        const guides = getGuides(lineGuideStops, itemBounds)
        const { presetX, presetY } = getAnchorPoints(guides)
        pos.x = presetX || pos.x
        pos.y = presetY || pos.y
      }
    }
    
    if (!isDrawing) {
      // Start drawing a new line
      setIsDrawing(true)
      setStartPoint(pos)
      setCurrentLine([pos.x, pos.y, pos.x, pos.y])
    } else {
      // Finish the line and create a material
      setIsDrawing(false)
      if (autoIncrementEnabled) {
        incrementPresets(selectedMaterialType as number)
      }
      
      // Create a new line between the start point and current position
      if (startPoint) {
        createNewLine(randomId(), startPoint, pos)
      }
      
      // Reset drawing state
      setStartPoint(null)
      setCurrentLine(null)
    }
  }

  const handleMouseMove = throttleRaf((evt: Konva.KonvaEventObject<MouseEvent>) => {
    // Skip processing for middle mouse button events
    if (evt.evt.buttons === 4) { // 4 corresponds to middle button being pressed
      return;
    }
    
    const stage = evt.target.findAncestor('Stage') as Konva.Stage;
    if (
      stage !== undefined &&
      stage.getPointerPosition() !== undefined
    ) {
      // Clear guide lines
      evt.target
        .getLayer()
        .find('.guid-line')
        .each((node: Konva.Node) => node.destroy())

      // If we're currently drawing a line, update the end point
      if (isDrawing && startPoint) {
        const pos = eventToCoordinates(evt)
        
        // Apply snapping if enabled
        if (props.snappingEnabled) {
          const lineGuideStops = getLineGuideStops(evt.target, pos, props.snappingDistance)
          const itemBounds = getObjectSnappingEdges(evt)
          if (itemBounds) {
            const guides = getGuides(lineGuideStops, itemBounds)
            const { presetX, presetY } = getAnchorPoints(guides)
            pos.x = presetX || pos.x
            pos.y = presetY || pos.y
            drawGuides(guides, evt.target)
          }
        }
        
        // Update the current line's end point
        setCurrentLine([startPoint.x, startPoint.y, pos.x, pos.y])
      }
    }
  })

  const eventToCoordinates = (evt: Konva.KonvaEventObject<MouseEvent>): Point => {
    const transform = evt.target.getAbsoluteTransform().copy()
    transform.invert()
    const stage = evt.target.findAncestor('Stage')
    // @ts-ignore
    const pos = transform.point(stage.getPointerPosition())
    return { x: round(pos.x, 2), y: round(pos.y, 2) }
  }

  const createNewLine = (overlay_id: string, startPos: Point, endPos: Point) => {
    dispatchAddMaterials({
      overlay_id,
      material_id: overlay_id,
      drawing_id: drawings.filter(drawing => drawing.displayName === selectedDrawing)[0].id,
      display_name: selectedDrawing as string,
      shape: 'line',
      shape_data: JSON.stringify({
        points: [startPos.x, startPos.y, endPos.x, endPos.y],
        strokeWidth: strokeWidth
      }),
      company_project_id: selectedProject as number,
      material_type_id: selectedMaterialType as number,
      value: descriptions.reduce((acc: { [key: string]: string }, name: string) => {
        if (selectedDrawing && name === 'Drawing Name') {
          acc[name] = selectedDrawing
        } else {
          // Check if there is a preset set for this.
          const preset = props.presets.find(preset => preset.description === name)
          if (preset) {
            acc[name] = preset.value
          } else {
            const descriptionData = props.descriptionsData.find(
              descData => descData.description === name
            )
            if (
              descriptionData &&
              (descriptionData.type === 'list' || descriptionData.type === 'button-list') &&
              descriptionData.choices.length > 0
            ) {
              acc[name] = descriptionData.choices[0]
            } else {
              acc[name] = ''
            }
          }
        }
        return acc
      }, {}),
    })
  }

  // No special mouse up handling needed for middle click
  // The default Zoomable component will handle it
  const handleMouseUp = (evt: Konva.KonvaEventObject<MouseEvent>) => {
    // Only needed for tool-specific operations
  }

  return (
    <Group onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="rgba(0, 0, 0, 0)"
        onMouseDown={handleMouseDown}
      />
      {/* Preview line while drawing - with pointer events disabled */}
      {currentLine && (
        <Line
          points={currentLine}
          stroke="red"
          strokeWidth={strokeWidth}
          dash={[5, 5]}
          lineCap="square"
          lineJoin="miter"
          strokeScaleEnabled={false}
          listening={false} // Critical: Disable mouse events on the preview line
        />
      )}
    </Group>
  )
}

const mapStateToProps = (
  state: ReduxStore.State,
  { selectedMaterialType }: { selectedMaterialType: string }
) => {
  const lineOptions = state.tool.options['line'] || { strokeWidth: 2 };

  return {
    descriptions: getDescriptions(state),
    descriptionsData: getDescriptionsData(state),
    drawings: getDrawings(state),
    snappingEnabled: getSnappingEnabled(state),
    presets: getMaterialTypePresets(state, selectedMaterialType),
    autoIncrementEnabled: getAutoIncrementPresetsEnabled(state),
    snappingDistance: getSnappingDistance(state),
    options: lineOptions
  };
}

const mapDispatchToProps = (dispatch: any) => ({
  dispatchAddMaterials: (m: ReduxStore.Materials.Data.IMaterial) => dispatch(action.addMaterial(m)),
  incrementPresets: (materialTypeId: number) => dispatch(incrementMaterialPresets(materialTypeId)),
})

export default withSelectedValues(
  // @ts-ignore
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(LineTool)
)